/**
 * Block renderer overrides and additions for the Bubble Machine.
 *
 * Three of these are the project's own types (`timeline`, `viewer` and
 * `bubble`) and two are overrides of core's `image` and `video`.
 *
 * They were `paragraph` blocks carrying a `variant` for a while, because
 * `remidus-api-core` used to drop any block type it did not know from the
 * served HTML, which would have made the timeline invisible to a crawler and to
 * a reader without scripts. api-core 1.16 lets a project contribute a
 * server-side renderer, so they are real types again: see `api/app/prerender.py`
 * for what each one renders as prose, and `admin.manifest.ts` for the fields
 * the editor offers.
 *
 * That mattered for editing as much as for crawling. As variants they all
 * shared one spec, so every one of them opened the same rich-text field in the
 * admin, editing the prose fallback rather than the thing on the page.
 */

import {
  CORE_RENDERERS,
  RemidusImage,
  type BlockRenderer,
} from '@remidus/web-core'

import { claimAnchor } from '../anchors'
import { videoFigure } from '../media-behaviour'
import { renderTimeline } from './timeline'
import { renderViewer } from './viewer'

/** A pull-out drawn as a bubble. The page's signature device. */
const renderBubble: BlockRenderer = (block) => {
  const data = block.data ?? {}
  const el = document.createElement('aside')
  el.className = 'skin-bubble-surface skin-pullout'

  const kicker = String(data.kicker ?? '').trim()
  if (kicker) {
    const node = document.createElement('p')
    node.className = 'skin-pullout__kicker'
    node.textContent = kicker
    el.append(node)
  }

  const heading = String(data.heading ?? '').trim()
  if (heading) {
    const node = document.createElement('p')
    node.className = 'skin-pullout__heading'
    node.textContent = heading
    el.append(node)
  }

  if (heading || kicker) claimAnchor(el, heading || kicker, heading ? kicker : undefined)

  const text = String(data.text ?? '').trim()
  if (text) {
    // Blank-line-separated paragraphs. Never innerHTML: this is author input.
    for (const paragraph of text.split(/\n{2,}/)) {
      const node = document.createElement('p')
      node.textContent = paragraph.trim()
      if (node.textContent) el.append(node)
    }
  }

  // A short list of things that had to be true. A real `ul`, because that is
  // what it is: three requirements read as three requirements to a screen
  // reader and to a parser, not as one sentence with semicolons in it.
  const checklist = Array.isArray(data.checklist) ? data.checklist : []
  const items = checklist
    .map((row) => (row && typeof row === 'object' ? String((row as { text?: unknown }).text ?? '') : ''))
    .map((item) => item.trim())
    .filter(Boolean)
  if (items.length > 0) {
    const list = document.createElement('ul')
    list.className = 'skin-checklist'
    for (const item of items) {
      const row = document.createElement('li')
      // A bubble, drawn here rather than pulled from an icon set, which the
      // brand guideline rules out. Same three parts the scene's own sprite has
      // — a film, a large specular and a small one, both off-centre — so the
      // marker belongs to the page rather than being a generic dot.
      //
      // aria-hidden because the list already says these are a list; a marker
      // that announces itself makes a screen reader read "bubble" three times
      // before the three things that actually matter.
      const ns = 'http://www.w3.org/2000/svg'
      const mark = document.createElementNS(ns, 'svg')
      mark.setAttribute('class', 'skin-checklist__bubble')
      mark.setAttribute('viewBox', '0 0 16 16')
      mark.setAttribute('aria-hidden', 'true')
      const film = document.createElementNS(ns, 'circle')
      film.setAttribute('cx', '8')
      film.setAttribute('cy', '8')
      film.setAttribute('r', '5.9')
      const gleam = document.createElementNS(ns, 'circle')
      gleam.setAttribute('class', 'skin-checklist__gleam')
      gleam.setAttribute('cx', '5.9')
      gleam.setAttribute('cy', '5.7')
      gleam.setAttribute('r', '1.45')
      const spark = document.createElementNS(ns, 'circle')
      spark.setAttribute('class', 'skin-checklist__gleam')
      spark.setAttribute('cx', '10.4')
      spark.setAttribute('cy', '10.1')
      spark.setAttribute('r', '0.62')
      mark.append(film, gleam, spark)
      const label = document.createElement('span')
      label.textContent = item
      row.append(mark, label)
      list.append(row)
    }
    el.append(list)
  }

  return el.childElementCount > 0 ? el : null
}


/** A standalone photograph or clip, as a two-column card.
 *
 * Core renders a media block as a full-width figure with the caption beneath.
 * That is right for a page whose pictures are the argument; here they are
 * evidence for a sentence, and a caption sitting under a full-bleed image reads
 * as an afterthought rather than as the thing it is explaining.
 *
 * So the block becomes a bubble card split in two: the media on one side, what
 * it shows on the other. Below 46rem it stacks, media first.
 */
function splitMedia(kind: 'image' | 'video'): BlockRenderer {
  return (block, ctx) => {
    const data = block.data ?? {}
    const item = ctx.resolveMedia(String(data.media_id ?? ''))
    if (!item) return CORE_RENDERERS[kind]?.(block, ctx) ?? null

    const figure = document.createElement('figure')
    figure.className = 'skin-bubble-surface skin-figure'

    const holder = document.createElement('div')
    holder.className = 'skin-figure__media'
    holder.append(
      kind === 'video'
        ? videoFigure(item)
        : RemidusImage(item),
    )
    figure.append(holder)

    const body = document.createElement('figcaption')
    body.className = 'skin-figure__body'
    const caption = String(data.caption ?? item.caption ?? '').trim()
    if (caption) {
      const lead = document.createElement('p')
      lead.className = 'skin-figure__caption'
      lead.textContent = caption
      body.append(lead)
    }
    // The alt text is the fullest description of the picture that exists, and
    // on a two-column card there is room to show it rather than hide it in an
    // attribute. Sighted readers get the detail; the attribute still carries it
    // for everyone else.
    const described = String(data.alt ?? item.alt ?? '').trim()
    if (described && described !== caption) {
      const note = document.createElement('p')
      note.className = 'skin-figure__described'
      note.textContent = described
      body.append(note)
    }
    if (body.childElementCount) figure.append(body)

    return figure
  }
}

export const blocks: Record<string, BlockRenderer> = {
  // Core types, restyled. A media block becomes a two-column card.
  image: splitMedia('image'),
  video: splitMedia('video'),

  // The project's own.
  bubble: renderBubble,
  timeline: renderTimeline,
  viewer: renderViewer,
}

export default blocks
